import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DataTable as DataTablePrimeReact } from 'primereact/datatable';
import { Column } from 'primereact/column';
import * as PropTypes from 'prop-types';
import 'primeicons/primeicons.css';
import './DataTableDesignEtat.scss';
import {
    Bouton,
    Carte,
    CarteBody,
    CarteContent,
    CarteDescription,
    CarteHeader,
    Col,
    Container,
    GroupeBouton,
    InputMi,
    Modale,
    ModaleContent,
    ModaleTitre,
    Option,
    Pagination,
    Row,
    SelectMi,
} from '@design-system-etat/dsfr-react';
import AffichageResponsive from '../AffichageResponsive';

const DataTableDesignEtat = ({
    records,
    columns,
    nombreTotal,
    recherche,
    pagination,
    rowClassName,
    clearFiltersButton,
    clearFiltersForce,
    customResultatName,
    exportButtonFonction,
    rowHover,
    onRowClick,
    hasPagination,
    areLignesSelectionnables,
    selectionListener,
    emptyMessage,
    rowExpansionTemplate,
    rechercheIcon,
    isLoading,
    pointDeRuptureResponsive,
}) => {
    const triParDefaut = { sortField: '', sortOrder: -1 };
    // state
    const [itemsParPage, setItemsParPage] = useState(pagination[0]);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState(triParDefaut);
    const [filter, setFilter] = useState({});
    const [doRercherche, setDoRecherche] = useState(false);
    const { register, reset } = useForm();

    const [lignesSelectionnees, setLignesSelectionnees] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);
    const [resultatModaleAffichee, setResultatModaleAffichee] = useState({});
    const [afficherFiltres, setAfficherFiltres] = useState(false);

    // recherche
    const lancerRecherche = () => {
        const queryParameters = {
            _limit: itemsParPage,
            _page: page + 1,
            ...filter,
        };

        if (sort) {
            // eslint-disable-next-line no-underscore-dangle
            queryParameters._sort = sort.sortField;
            // eslint-disable-next-line no-underscore-dangle
            queryParameters._order = sort.sortOrder === 1 ? 'asc' : 'desc';
        }
        recherche(queryParameters);
    };
    useEffect(() => {
        if (doRercherche) {
            lancerRecherche();
            setDoRecherche(false);
        }
    });

    useEffect(() => {
        setLignesSelectionnees([]);
    }, [records]);

    useEffect(() => {
        if (selectionListener) {
            selectionListener(lignesSelectionnees);
        }
    }, [lignesSelectionnees]);

    useEffect(() => {
        setSort(triParDefaut);
        setPage(0);
        setDoRecherche(true);
    }, [itemsParPage]);

    const onClearFilters = () => {
        setPage(0);
        setSort(triParDefaut);
        setItemsParPage(10);
        setFilter({});
        const objectForReset = {};
        columns.forEach((column) => {
            objectForReset[column.field] = null;
        });
        reset(objectForReset);
        setDoRecherche(true);
    };

    useEffect(() => {
        if (clearFiltersForce) {
            onClearFilters();
        }
    }, [clearFiltersForce]);

    const onChangePage = (pageToGo) => {
        setPage(pageToGo - 1);
        setDoRecherche(true);
    };

    const onChangeSort = (sortToChange) => {
        setSort(sortToChange);
        setDoRecherche(true);
    };

    const onChangeFilter = (event) => {
        if (event.target.value) {
            const nouveauxFiltres = { ...filter };
            nouveauxFiltres[event.target.id] = event.target.value;
            setFilter(nouveauxFiltres);
        } else if (filter[event.target.id]) {
            const nouveauxFiltres = { ...filter };
            delete nouveauxFiltres[event.target.id];
            setFilter(nouveauxFiltres);
        }
    };

    const onFilterEnterKeyUp = (event) => {
        if (event.key === 'Enter' || event.charCode === 13) {
            event.preventDefault();
            setPage(0);
            lancerRecherche();
        }
    };

    const getFilterTemplate = (column, estMobile) => {
        const labelFiltre = estMobile && column.header;
        if (
            (rechercheIcon === true && !column.filterType) ||
            (column.filterType === 'text' && rechercheIcon === true)
        ) {
            return (
                <InputMi
                    label={labelFiltre}
                    icone="fr-fi-search-line"
                    {...register(column.field)}
                    onChange={(event) => onChangeFilter(event)}
                    onKeyUp={(event) => onFilterEnterKeyUp(event)}
                    maskFonction={column.maskFonction}
                />
            );
        }
        if (!column.filterType || column.filterType === 'text') {
            return (
                <InputMi
                    label={labelFiltre}
                    {...register(column.field)}
                    onChange={(event) => onChangeFilter(event)}
                    onKeyUp={(event) => onFilterEnterKeyUp(event)}
                    maskFonction={column.maskFonction}
                />
            );
        }

        if (column.filterType === 'list') {
            return (
                <SelectMi
                    label={labelFiltre}
                    {...register(column.filterName ?? column.field)}
                    onChange={(event) => {
                        onChangeFilter(event);
                        setDoRecherche(true);
                    }}
                >
                    {column.filterList.map((option) => (
                        <Option key={option.value ?? 0} value={option.value}>
                            {option.libelle}
                        </Option>
                    ))}
                </SelectMi>
            );
        }
        return null;
    };

    const creerChampsTri = () => {
        return (
            <>
                <Col col={12} md={6}>
                    <SelectMi
                        name="champ-tri"
                        label="Trier selon"
                        onChange={(event) =>
                            onChangeSort({ ...sort, sortField: event.target.value })
                        }
                        value={sort.sortField}
                    >
                        <Option key="aucunChamp" value="" />
                        {columns
                            .filter((column) => column.sortable)
                            .map((column) => (
                                <Option key={column.field} value={column.field}>
                                    {column.header}
                                </Option>
                            ))}
                    </SelectMi>
                </Col>
                <Col col={12} md={6}>
                    <SelectMi
                        name="ordre-tri"
                        label="Ordre du tri"
                        onChange={(event) =>
                            onChangeSort({ ...sort, sortOrder: parseInt(event.target.value, 10) })
                        }
                        value={sort.sortOrder}
                    >
                        <Option key="croissant" value="1">
                            Tri croissant
                        </Option>
                        <Option key="decroissant" value="-1">
                            Tri décroissant
                        </Option>
                    </SelectMi>
                </Col>
            </>
        );
    };

    /** On vérifie que la ligne en entrée n'est pas déjà sélectionnée.
     * si elle l'est on la dé-sélectionne, sinon on la sélectionne */
    const majSelection = (value) => {
        if (value && value.length > 0) {
            const ligneAEnlever = lignesSelectionnees.find((ligne) => ligne === value[0]);
            if (ligneAEnlever) {
                setLignesSelectionnees(
                    lignesSelectionnees.filter((ligne) => ligne !== ligneAEnlever),
                );
            } else {
                setLignesSelectionnees(lignesSelectionnees.concat(value));
            }
        }
    };

    const genererBody = (rowData, estMobile) => {
        let afficherCaseCochee = false;

        lignesSelectionnees.forEach((ligneSelectionnee) => {
            if (rowData === ligneSelectionnee) {
                afficherCaseCochee = true;
            }
        });
        return (
            <div className={`fr-checkbox-group fr-checkbox-group--${estMobile ? 'lg' : 'sm'}`}>
                <input
                    type="checkbox"
                    checked={afficherCaseCochee}
                    onChange={() => majSelection([rowData])}
                />
                {/* Fonctionnement du design etat qui va à l'encontre de la règle. On ne peut pas non plus avoir un id unique */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                <label className="fr-label" />
            </div>
        );
    };

    const creerCarteResultat = (resultat) => {
        return (
            <Col col={12} md={6} className="padding-carte">
                <Carte noEnlargeLink>
                    <CarteBody>
                        <CarteContent>
                            <CarteDescription>
                                {columns
                                    .filter((column) => column.header)
                                    .map((column) => (
                                        <p>
                                            <b>{column.header} : </b>
                                            {resultat[column.field]}
                                        </p>
                                    ))}
                            </CarteDescription>
                        </CarteContent>
                    </CarteBody>
                    {(areLignesSelectionnables || rowExpansionTemplate) && (
                        <CarteHeader>
                            <Container>
                                <Row className="boutons-carte">
                                    {areLignesSelectionnables && (
                                        <Col>
                                            <div
                                                onClick={() => majSelection([resultat])}
                                                onKeyDown={() => majSelection([resultat])}
                                                role="button"
                                                tabIndex="0"
                                            >
                                                {genererBody(resultat, true)}
                                            </div>
                                        </Col>
                                    )}
                                    {rowExpansionTemplate && (
                                        <>
                                            <Col offset={8}>
                                                <button
                                                    type="button"
                                                    aria-label="Plus d'informations"
                                                    className="fr-icon-information-fill icone-information"
                                                    onClick={() =>
                                                        setResultatModaleAffichee(resultat)
                                                    }
                                                />
                                            </Col>
                                            {resultatModaleAffichee === resultat && (
                                                <Modale
                                                    id="modale-information"
                                                    closeCallback={() =>
                                                        setResultatModaleAffichee({})
                                                    }
                                                    taille="lg"
                                                >
                                                    <ModaleTitre idModale="modale-information">
                                                        Informations
                                                    </ModaleTitre>
                                                    <ModaleContent>
                                                        {rowExpansionTemplate(resultat)}
                                                    </ModaleContent>
                                                </Modale>
                                            )}
                                        </>
                                    )}
                                </Row>
                            </Container>
                        </CarteHeader>
                    )}
                </Carte>
            </Col>
        );
    };

    const dropdownOptions = pagination.map((value) => ({ libelle: value, value }));

    const majSelectionGlobale = () => {
        if (lignesSelectionnees.length === records.length) {
            setLignesSelectionnees([]);
        } else {
            setLignesSelectionnees(records);
        }
    };

    const genererHeader = () => {
        let afficherCaseCochee = false;
        if (lignesSelectionnees.length === records.length) {
            afficherCaseCochee = true;
        }
        return (
            <div className="fr-checkbox-group fr-checkbox-group--sm">
                <input
                    type="checkbox"
                    id="checkboxHeader"
                    name="checkboxHeader"
                    checked={afficherCaseCochee}
                    onChange={() => majSelectionGlobale(itemsParPage)}
                />
                {/* Fonctionnement du design etat qui va à l'encontre de la règle */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="fr-label" htmlFor="checkboxHeader" />
            </div>
        );
    };

    const addExpandedRow = (row) => {
        if (expandedRows.indexOf(row) === -1) {
            setExpandedRows([...expandedRows, row]);
        }
    };

    const removeExpandedRow = (row) => {
        const index = expandedRows.indexOf(row);
        if (index !== -1) {
            expandedRows.splice(index, 1);
            setExpandedRows([...expandedRows]);
        }
    };

    const getExpanderColumnTemplate = (row) => {
        const estOuverte = expandedRows.indexOf(row) !== -1;

        return (
            <Bouton
                icone={estOuverte ? 'fr-fi-subtract-line' : 'fr-fi-add-line'}
                taille="sm"
                niveau="secondaire"
                onClick={(event) => {
                    if (estOuverte) {
                        removeExpandedRow(row);
                    } else {
                        addExpandedRow(row);
                    }
                    event.stopPropagation();
                }}
            />
        );
    };

    // eslint-disable-next-line no-shadow
    const getTotalPageReel = (nombreTotal, itemsParPage) => {
        // Calcul du nombre de pages.
        const totalPage = Math.ceil(nombreTotal / itemsParPage);

        /**
         * Si on a zéro page, on retourne qu'il n'y a qu'une seule page. Ceci étant pour empêcher un dysfonctionnement
         * du design.
         */
        if (totalPage === 0) {
            return 1;
        }

        return totalPage;
    };

    return (
        <>
            <Row goutierre alignementVertical="middle">
                <Col col={12} sm={5}>
                    {clearFiltersButton && (
                        <Bouton
                            onClick={onClearFilters}
                            niveau="secondaire"
                            label="Réinitialiser les filtres"
                        />
                    )}{' '}
                    {exportButtonFonction && (
                        <Bouton
                            onClick={exportButtonFonction}
                            niveau="secondaire"
                            label="Exporter"
                        />
                    )}
                </Col>
                <Col col={6} sm={2}>
                    {nombreTotal} {customResultatName || 'résultat'}
                    {nombreTotal > 1 && 's'}
                </Col>
                {hasPagination && (
                    <Col col={6} sm={5} className="right">
                        <div style={{ display: 'inline-block', textAlign: 'right' }}>
                            <SelectMi
                                label="Résultats par page"
                                onChange={(event) => setItemsParPage(event.target.value)}
                            >
                                {dropdownOptions.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.libelle}
                                    </Option>
                                ))}
                            </SelectMi>
                        </div>
                    </Col>
                )}
            </Row>

            <AffichageResponsive
                pointDeRupture={pointDeRuptureResponsive}
                composantDesktop={
                    <Container margeExterne={false}>
                        <DataTablePrimeReact
                            className="fr-table fr-table--bordered"
                            lazy
                            value={records}
                            rows={records.length}
                            totalRecords={nombreTotal}
                            first={page * itemsParPage}
                            page={page}
                            onSort={onChangeSort}
                            sortField={sort.sortField}
                            sortOrder={sort.sortOrder}
                            rowClassName={rowClassName}
                            rowHover={rowHover}
                            onRowClick={onRowClick}
                            selection={lignesSelectionnees}
                            onSelectionChange={(e) => majSelection(e.value)}
                            selectionMode="multiple"
                            emptyMessage={emptyMessage}
                            expandedRows={rowExpansionTemplate ? expandedRows : null}
                            rowExpansionTemplate={rowExpansionTemplate}
                            filterDisplay="row"
                            loading={isLoading}
                        >
                            {areLignesSelectionnables && (
                                <Column
                                    key="checkbox"
                                    field="checkbox"
                                    header={genererHeader()}
                                    body={(rowData) => genererBody(rowData, false)}
                                    style={{ paddingLeft: 0, paddingRight: 0, width: 30 }}
                                />
                            )}

                            {columns.map((column) => {
                                const otherAttributes = {};

                                if (column.expander) {
                                    otherAttributes.body = getExpanderColumnTemplate;
                                }

                                if (column.template) {
                                    otherAttributes.body = column.template;
                                }

                                if (column.filter) {
                                    otherAttributes.filterMatchMode = 'contains';
                                    otherAttributes.showFilterMenu = false;
                                    otherAttributes.filterElement = getFilterTemplate(
                                        column,
                                        false,
                                    );
                                }
                                if (column.bodyStyle) {
                                    otherAttributes.bodyStyle = column.bodyStyle;
                                }
                                if (column.headerStyle) {
                                    otherAttributes.headerStyle = column.headerStyle;
                                }

                                return (
                                    <Column
                                        key={column.field}
                                        field={column.field}
                                        header={column.header}
                                        sortable={column.sortable}
                                        filter={column.filter}
                                        {...otherAttributes}
                                    />
                                );
                            })}
                        </DataTablePrimeReact>
                    </Container>
                }
                composantMobile={
                    <>
                        <Row goutierre className="margin-top-sm">
                            <Col>
                                <GroupeBouton isHorizontal>
                                    <Bouton
                                        onClick={() => setAfficherFiltres(!afficherFiltres)}
                                        niveau="secondaire"
                                        label={`${
                                            afficherFiltres ? 'Masquer' : 'Afficher'
                                        } les filtres`}
                                    />
                                    {areLignesSelectionnables && (
                                        <Bouton
                                            onClick={() => majSelectionGlobale(itemsParPage)}
                                            niveau="secondaire"
                                            label="Tout sélectionner"
                                        />
                                    )}
                                </GroupeBouton>
                            </Col>
                        </Row>
                        <Container margeExterne={false}>
                            {afficherFiltres && (
                                <>
                                    <Row className="margin-top-md">
                                        <Col col={12}>
                                            <h5>Trier</h5>
                                        </Col>
                                    </Row>
                                    <Row goutierre>{creerChampsTri()}</Row>
                                    <Row className="margin-top-md">
                                        <Col col={12}>
                                            <h5>Filtrer</h5>
                                        </Col>
                                    </Row>
                                    <Row goutierre>
                                        {columns
                                            .filter((column) => column.filter)
                                            .map((column) => (
                                                <Col col={12} md={6}>
                                                    {getFilterTemplate(column, true)}
                                                </Col>
                                            ))}
                                    </Row>
                                </>
                            )}
                            <Row className="liste-resultats-mobile">
                                {records.map((resultat) => creerCarteResultat(resultat))}
                            </Row>
                        </Container>
                    </>
                }
            />

            {hasPagination && (
                <Row goutierre alignementHorizontal="center">
                    <Pagination
                        pageCourante={page + 1}
                        totalPages={getTotalPageReel(nombreTotal, itemsParPage)}
                        changePage={onChangePage}
                    />
                </Row>
            )}
        </>
    );
};

DataTableDesignEtat.propTypes = {
    records: PropTypes.array,
    nombreTotal: PropTypes.number,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string,
            header: PropTypes.string,
            sortable: PropTypes.bool,
            filter: PropTypes.bool,
            template: PropTypes.func,
        }),
    ).isRequired,
    recherche: PropTypes.func.isRequired,
    clearFiltersButton: PropTypes.bool,
    pagination: PropTypes.array,
    rowClassName: PropTypes.func,
    clearFiltersForce: PropTypes.bool,
    customResultatName: PropTypes.string,
    exportButtonFonction: PropTypes.func,
    rowHover: PropTypes.bool,
    onRowClick: PropTypes.func,
    hasPagination: PropTypes.bool,
    areLignesSelectionnables: PropTypes.bool,
    selectionListener: PropTypes.func,
    emptyMessage: PropTypes.string,
    onRowToggleCustom: PropTypes.func,
    rowExpansionTemplate: PropTypes.func,
    rechercheIcon: PropTypes.bool,
    isLoading: PropTypes.bool,
    pointDeRuptureResponsive: PropTypes.string,
};

DataTableDesignEtat.defaultProps = {
    records: [],
    nombreTotal: 0,
    clearFiltersButton: false,
    pagination: [10, 30, 50, 100],
    rowClassName: null,
    clearFiltersForce: false,
    customResultatName: null,
    rowHover: false,
    hasPagination: true,
    emptyMessage: null,
    isLoading: false,
    pointDeRuptureResponsive: 'xl',
};

export default DataTableDesignEtat;
